import {
  Box,
  Button,
  Card,
  CardBody,
  FormControl,
  FormLabel,
  Heading,
  Image,
  Input,
  InputGroup,
  InputLeftElement,
  Stack,
  Text,
} from '@chakra-ui/react';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { FiCheck, FiMail } from 'react-icons/fi';
import LoginLayout from '@/components/login/LoginLayout';
import useSmartToast from '@/hooks/SmartToast';
import { authClient } from '@/lib/auth';
import { toTitleCase } from '@/utils/utils';
import { isEmail } from '@/utils/validate';

export default function ForgotPassword() {
  const [isLoading, setLoading] = useState(false);
  const toast = useSmartToast();
  const router = useRouter();
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);

  const handleSubmit = async e => {
    e.preventDefault();
    if (!isEmail(email)) return toast.error(null, 'Invalid email');

    setLoading(true);

    try {
      await authClient.requestPasswordReset({
        email: email.trim(),
      });

      toast.success('Password reset link was sent to your email.');
      setSent(true);
    } catch (error) {
      console.error(error);
      toast.error(error, 'Error resetting password');
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!router.isReady) return;

    if (!['admin', 'client', 'vendor'].includes(router.query.id as any)) {
      toast.error(null, 'Invalid link. Please try again');
      return;
    }
  }, [router.query]);

  if (sent)
    return (
      <Stack h='full' gap={0} justifyContent='center' alignItems='center'>
        <Card size='lg'>
          <CardBody
            as={Stack}
            gap='2'
            alignItems='center'
            color='green.500'
            textAlign='center'
          >
            <Box
              rounded='full'
              width='24'
              height='24'
              borderColor='green.500'
              borderWidth='2px'
              mb='4'
              display='flex'
              justifyContent='center'
              alignItems='center'
            >
              <FiCheck size='48' />
            </Box>
            <Heading size='lg' fontWeight='semibold'>
              Reset Link Sent
            </Heading>
            <Text mt='2' fontSize='sm' fontWeight='medium' color='gray.500'>
              Password reset link was sent to your email.
              <br />
              Please check your inbox and spam folder.
            </Text>
          </CardBody>
        </Card>
      </Stack>
    );
  else
    return (
      <Stack h='full' mt='28' p='8' alignItems='center' minW='lg'>
        <Stack
          as='form'
          w='full'
          px='7'
          py='5'
          gap='4'
          maxW='md'
          rounded='lg'
          border='1px solid'
          borderColor='gray.300'
          bg='white'
          onSubmit={handleSubmit}
        >
          <Stack gap='2'>
            <Image
              src='/images/brand-logo.png'
              alt='Procura Logo'
              w='fit-content'
              h='10'
              alignSelf='center'
            />
            <Heading size='md' mt='2' fontWeight='semibold' color='gray.800'>
              Hello {toTitleCase(router.query.id as string)}!
            </Heading>
            <Text fontWeight='medium' color='gray.400'>
              Forgot your password? Let&apos;s get you back in.
            </Text>
          </Stack>

          <FormControl>
            <FormLabel>Email</FormLabel>
            <InputGroup size='lg'>
              <InputLeftElement>
                <FiMail />
              </InputLeftElement>
              <Input
                type='text'
                placeholder='sales@yourcompany.com'
                value={email}
                onChange={e => setEmail(e.target.value)}
              />
            </InputGroup>
          </FormControl>

          <Button
            type='submit'
            variant='procura'
            size='lg'
            w='full'
            mt='4'
            py='4'
            fontSize='sm'
            isLoading={isLoading}
          >
            Send Password Reset Link
          </Button>
        </Stack>
      </Stack>
    );
}

ForgotPassword.getLayout = page => <LoginLayout>{page}</LoginLayout>;
